<script>
  // TODO - add a popup to review all items in order - move that to the team details page
  import Swal from 'sweetalert2';

  import { onMount, getContext } from 'svelte';
  import { goto } from '@sapper/app';

  import LoadingSpinner from '../LoadingSpinner.svelte';
  import ColoredButton from './ColoredButton.svelte';
  import Button from '../Button.svelte';
  import Modal from '../Modal.svelte';
  import ItemSummary from './ItemSummary.svelte';

  import determineDeliveryDate from '../../utils/determineDeliveryDate.js';
  import { buildProductSizeData } from '../../utils/productSizeUtils.js';
  import { generateItemSummaries } from '../../utils/itemSummaries.js';

  import { designState } from '../../stores/Designer.js';
  import { konvaStageId, hidePreview } from '../../stores/Preview.js';
  import { productDataStore } from '../../stores/ProductData.js';
  import { teamDetails } from '../../stores/TeamDetails.js';
  import { cartDetails, shoppingCartId } from '../../stores/Cart.js';
  import { environment } from '../../environment/environment';

  export let konvaStage;

  const defaultBulkDiscounts = getContext('defaultBulkDiscounts');

  let showLoading = false;
  let isBrowser;
  let designMode;
  let arrayOfSelectedProducts;
  let teamDetailsValid = false;
  let numberInputRequired;
  let nameInputRequired;
  let noSizesSelected = false;
  let itemSummaryData;

  const itemTypes = ['top', 'bottom', 'hat', 'socks', 'accessory'];
  // const itemTypes = environment.PRODUCT_TYPES // Why is this not working, but above does?

  function setupAndValidateDetails() {
    arrayOfSelectedProducts = designState.arrayOfSelectedProducts($konvaStageId); // If we have konvaStage then we can use the id store
    designMode = designState.getDesignMode($konvaStageId);
    numberInputRequired = designState.isNumberInputRequired($konvaStageId, arrayOfSelectedProducts);
    nameInputRequired = designState.isNameInputRequired($konvaStageId, arrayOfSelectedProducts);

    // Now check if team details are valid
    teamDetailsValid = teamDetails.validateTeamDetails(arrayOfSelectedProducts, numberInputRequired, nameInputRequired);
    let slug = $productDataStore[$productDataStore.showing].slug;
    if (teamDetailsValid === false) {
      goto(`/design/${slug}/team-details?valid=false`);
    }
  }

  $: if (konvaStage) {
    // Once the stage is ready we can hide preview
    $hidePreview = true;
  }

  let productSizeData = {
    topSizes: null,
    bottomSizes: null,
    hatSizes: null,
    socksSizes: null,
    accessorySizes: null,
  };

  let orderComments = '';

  let showModal = false;
  let cartPreviewImage;

  async function addToCart() {
    showLoading = true;

    // Determine mode - i.e. if we are editing or new design
    let mode = designState.getDesignMode($konvaStageId);

    // check if shopping cart id exists - should already be here
    if (!$shoppingCartId) {
      // create new shopping cart
      cartDetails.createShoppingCart();
    }

    let designIndex = designState.determineDesignIndex($konvaStageId);

    // Determine small preview image for cart item
    // e.g. front pic of top
    if (itemSummaryData.top.count > 0) {
      cartPreviewImage = itemSummaryData.top.frontPreview;
    } else if (itemSummaryData.bottom.count > 0) {
      cartPreviewImage = itemSummaryData.bottom.frontPreview;
    } else if (itemSummaryData.hat.count > 0) {
      cartPreviewImage = itemSummaryData.hat.frontPreview;
    } else if (itemSummaryData.socks.count > 0) {
      cartPreviewImage = itemSummaryData.socks.frontPreview;
    } else if (itemSummaryData.accessory.count > 0) {
      cartPreviewImage = itemSummaryData.accessory.frontPreview;
    }

    let cartItemId;
    if (mode !== 'edit') {
      cartItemId = await cartDetails.addItemToCart(
        $shoppingCartId,
        orderComments,
        itemSummaryData.totalPrice,
        $designState[designIndex],
        cartPreviewImage,
        itemSummaryData, // Include item summary so we can use this data for mini cart and cart pages
        $teamDetails,
      );
    }

    if (mode === 'edit' || mode === 'db-edit') {
      // Update the item
      cartItemId = designState.getDesignCartItemId($konvaStageId);
      await cartDetails.updateItemInCart(
        cartItemId,
        $shoppingCartId,
        orderComments,
        itemSummaryData.totalPrice,
        $designState[designIndex],
        cartPreviewImage,
        itemSummaryData, // Include item summary so we can use this data for mini cart and cart pages
        $teamDetails,
      );

      // Delete all associated design data (designItems with FKey reference to this cartItem). designItems will be rebuilt and submitted with the cartItem below.
      await cartDetails.deleteDesignItems(cartItemId);
    }

    // prepare array of design data
    let designData = [];
    // also create an items array for Google Analytics
    let items = [];

    // loop through selected items and add details to designData
    // and update cart item summary
    for (let i = 0; i < arrayOfSelectedProducts.length; i++) {
      let item = arrayOfSelectedProducts[i];
      let design = $designState[designIndex][item];
      let frontSelector = item + '-preview-front';
      let backSelector = item + '-preview-back';

      let frontImageSrc = document.getElementById(frontSelector)?.src;
      let backImageSrc = document.getElementById(backSelector)?.src;

      if (!frontImageSrc && !backImageSrc) {
        // Can occur if user has selected shorts, but then not selected a size
        console.log('Image not found for item: ', item);
        continue;
      }

      let designDataItem = {
        cartItemId: cartItemId,
        productSlug: design.product.slug,
        product: JSON.stringify(design.product),
        productName: design.product.name,
        colourId: design.colour?.id, // Id can be undefined for custom sublimation colours
        colourName: design.colour.displayName,
        colour: JSON.stringify(design.colour),
        sublimationTemplate: JSON.stringify(design.sublimationTemplate),
        designElements: JSON.stringify(design.designElements),
        teamDetails: JSON.stringify($teamDetails),
        quantity: itemSummaryData[item].count,
        sizeSummary: itemSummaryData[item].sizeSummary,
        totalPrice: itemSummaryData[item].total,
        isCustomised: itemSummaryData[item].isCustomised,
        frontPreviewB64: frontImageSrc,
        backPreviewB64: backImageSrc,
      };
      designData.push(designDataItem);

      let itemObject = {
        item_name: design.product.name,
        price: (design.product.price / 100).toFixed(2),
        quantity: itemSummaryData[item].count,
        currency: 'AUD',
        item_category: design.product.category,
        item_category2: design.product.collection,
        item_category3: item,
        item_category4: itemSummaryData[item].isCustomised,
        item_variant: design.colour.displayName,
      };
      items.push(itemObject);
    }

    // Send add to cart event to Google Analytics
    if (mode !== 'db-edit' && mode !== 'edit') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'AUD',
          value: (itemSummaryData.totalPrice / 100).toFixed(2),
          items: items,
        },
      });

      // Add to cart event to Facebook Pixel
      if (typeof fbq !== 'undefined') {
        fbq('track', 'AddToCart', {
          content_name: 'Add to Cart',
          content_category: 'Add to Cart',
          content_ids: items.map((item) => item.item_name),
          contents: JSON.stringify(items),
          content_type: 'product',
          value: (itemSummaryData.totalPrice / 100).toFixed(2),
          currency: 'AUD',
        });
      }
    }

    await cartDetails.addDesignItemToCartItem(designData);

    // Now the item is in the cart clear the design state
    designState.clearDesignState();

    if (mode === 'db-edit') {
      await Swal.fire({
        title: 'Item Updated',
        text: 'Close this Tab, Return to the Database and Refresh the page.',
        icon: 'success',
        showCancelButton: false,
        confirmButtonText: 'ok',
      }).then((result) => {
        if (result.isConfirmed) {
          window.close();
        }
      });

      window.close();
    }

    if (mode === 'edit') {
      // Tidy up the states
      designState.restoreDesignState();
      teamDetails.restoreTeamDetails();

      // If there was any previous design data before editing reset the stage id to match
      let savedDesignId = designState.getDesignId();
      if (savedDesignId) {
        $konvaStageId = savedDesignId;
      }

      // Go straight back to cart - do not show modal
      await goto('/cart');
    }

    showLoading = false;
    // Show popup go to cart or continue shopping
    showModal = true;
  }

  onMount(async () => {
    setupAndValidateDetails();
    productSizeData = buildProductSizeData(arrayOfSelectedProducts, $productDataStore);
    const { itemSummary, noSizesSelected: noSizes } = await generateItemSummaries({
      designId: $designState[0]?.id,
      designState,
      productDataStore: $productDataStore,
      teamDetails: $teamDetails,
      numberInputRequired,
      nameInputRequired,
      defaultBulkDiscounts,
    });
    itemSummaryData = itemSummary;
    noSizesSelected = noSizes;
    isBrowser = true;
  });
</script>

{#if isBrowser}
  <Modal show={showModal} allowClose={false}>
    <div class="modal-container">
      <div class="modal-header">
        <h2>Your order has been added to your cart</h2>
      </div>
      <div class="modal-body">
        <div class="modal-body-content">
          <img class="preview-image" src={cartPreviewImage} alt="" />
        </div>
      </div>
      <div class="modal-footer">
        <div class="modal-button">
          <Button label="Continue Shopping" type="black-o" width="inherit" clickFn={() => goto('/products')} />
        </div>
        <div class="modal-button">
          <Button label="View Cart" width="inherit" clickFn={() => goto('/cart')} />
        </div>
      </div>
    </div>
  </Modal>

  {#if teamDetailsValid === true}
    <div class="container">
      <div class="loading" class:show={showLoading}>
        <LoadingSpinner />
      </div>

      <div class="summary">
        {#if noSizesSelected}
          <div class="no-items-selected">
            <div class="no-items-selected-text">
              <div class="no-items-selected-text-header">No items selected</div>
              <div class="no-items-selected-text-body">Please go to step 1 and begin a new design</div>
            </div>
          </div>
        {:else}
          {#if itemSummaryData}
            {#each itemTypes as key}
              {#if itemSummaryData[key].count > 0}
                <ItemSummary itemKey={key} summary={itemSummaryData[key]} productName={$productDataStore[key].name} />
              {/if}
            {/each}
          {/if}

          {#if itemSummaryData.includesEmbroidery && itemSummaryData.totalPrice / 100 < 1000}
            <div class="embroidery-note">
              <b>NOTE:</b> Your order contains <b>embroidery</b>. There is a first time setup fee of <b>$50.00</b> for
              new embroidered logos.<br />
              If applicable, the setup fee will be <b>invoiced separately</b> after your order is placed.<br />
            </div>
          {/if}

          <div class="order-total">
            Total: ${(itemSummaryData.totalPrice / 100).toFixed(2)}
          </div>

          <div class="textarea">
            <textarea
              bind:value={orderComments}
              class="textarea-input"
              placeholder="Add a note to your order"
              rows="5"
              cols="50"
            />
          </div>

          <div class="estimated-shipping">
            <div class="estimated-shipping-heading">Estimated Shipping</div>
            {#each $productDataStore[$productDataStore.showing].deliveryTimes as deliveryTime}
              <div class="delivery-time">
                {deliveryTime.DeliveryTime.name} - {determineDeliveryDate(deliveryTime.DeliveryTime.deliveryTime)}
              </div>
            {/each}
            {#if environment.SHOW_CHRISTMAS_MESSAGE}
              <div class="delivery-time">
                <b>Christmas Delivery:</b> Orders placed after 10th December won't ship until mid January.
              </div>
            {/if}
          </div>

          <div class="add-to-cart">
            {#if designMode === 'edit'}
              <ColoredButton label={'Update Cart'} type={'green'} clickFn={addToCart} size={'lg'} />
            {:else if designMode === 'db-edit'}
              <ColoredButton label={'Update Order'} type={'green'} clickFn={addToCart} size={'lg'} />
            {:else}
              <ColoredButton label={'Add To Cart'} type={'green'} clickFn={addToCart} size={'lg'} />
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/if}
{:else}
  <div class="container">
    <div class="loading">
      <LoadingSpinner />
    </div>
  </div>
{/if}

<style>
  .container {
    background-color: white;
    position: relative;
    padding: 2rem;
    min-height: 500px;
    padding-bottom: 50px;
  }

  .summary {
    display: flex;
    flex-direction: column;
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
  }

  .preview-image {
    max-width: 50%;
    width: auto;
    height: auto;
  }

  .order-total {
    width: 100%;
    display: flex;
    justify-content: right;
    margin-right: 1rem;
    color: #4a4a4a;
    font-weight: 700;
  }

  .embroidery-note {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }

  .textarea {
    margin-top: 2rem;
    width: 100%;
  }

  textarea {
    width: 100%;
    border-radius: 1rem;
    border: 2px dashed #d6d3d3;
    padding: 1rem;
    font-size: 1.4rem;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    resize: none;
    overflow: hidden;
  }

  textarea:focus {
    outline: none;
    border: 2px dashed #000;
  }

  .estimated-shipping {
    width: 100%;
    text-align: center;
    padding: 2rem 0;
  }

  .estimated-shipping-heading {
    font-family: 'Oswald';
    text-transform: uppercase;
    color: #fe5900;
  }

  .delivery-time {
    margin-top: 0.8rem;
    color: #666;
    text-transform: capitalize;
    font-size: 1.4rem;
  }

  .add-to-cart {
    width: 80%;
    margin-top: 1rem;
    margin-left: auto;
    margin-right: auto;
  }

  .loading {
    z-index: 100;
    display: none;
    padding-top: 10rem;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    background: rgba(255, 255, 255, 0.9);
  }

  .show {
    display: flex;
  }

  .modal-container {
    text-align: center;
  }

  .modal-header {
    font-family: 'Oswald';
    font-weight: 700;
    color: #4a4a4a;
    text-transform: uppercase;
    font-size: 1.4rem;
    margin-bottom: 2rem;
    text-align: center;
  }

  .modal-footer {
    margin-top: 2rem;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
  }

  .no-items-selected {
    font-family: 'Oswald';
    font-size: 2rem;
    text-align: center;
    margin-top: 10rem;
    color: #fe5900;
  }

  @media screen and (min-width: 900px) {
    .embroidery-note {
      font-size: 1.4rem;
    }
  }
</style>
